// Admin — Cross-Site Leak Alarm dashboard. Shows the last audit run,
// per-supervisor leak counts, and lets a master admin trigger the audit
// on demand. Any red row here means a scoped supervisor is seeing data
// outside their scope.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldCheck, ShieldAlert, PlayCircle } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AdminScopeLeaksPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchData = useCallback(async () => {
    // Gate the fetch until auth has hydrated + user is a master admin —
    // avoids the double-403 toast flash the testing agent observed.
    if (!token || user?.role !== 'master_admin') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/scope-leaks`, { headers });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load scope-leak audit');
    } finally { setLoading(false); }
  }, [headers, token, user?.role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/scope-leaks/run-now`, {}, { headers });
      toast.success('Audit complete — refreshing…');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Audit run failed');
    } finally { setRunning(false); }
  };

  const records = data?.records || [];
  const leaked = data?.leaked || [];
  const lastRun = data?.last_run;
  const cleanCount = records.length - leaked.length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="scope-leaks-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Cross-Site Leak Alarm</h1>
            <p className="text-white/40 text-sm mt-0.5 max-w-2xl">
              Nightly (05:15 UTC) synthetic-supervisor audit that verifies every scoped supervisor
              only sees agents inside their <span className="text-teal-300 font-medium">site_scope</span>.
              A red row means at least one core endpoint returned an agent outside the scope — investigate immediately.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 rounded-lg px-3 py-1.5"
              data-testid="scope-leaks-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={runNow}
              disabled={running}
              className="inline-flex items-center gap-1.5 text-sm bg-teal-500 hover:bg-teal-400 disabled:opacity-50 text-white rounded-lg px-3 py-1.5 font-semibold"
              data-testid="scope-leaks-run-now"
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
              Run audit now
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Supervisors audited" value={records.length} />
          <SummaryCard label="Clean" value={cleanCount} tone="emerald" icon={ShieldCheck} />
          <SummaryCard label="Leaked" value={leaked.length} tone={leaked.length > 0 ? 'rose' : 'slate'} icon={ShieldAlert} />
          <SummaryCard label="Total leaks" value={leaked.reduce((a, r) => a + (r.leak_count || 0), 0)} tone={leaked.length > 0 ? 'rose' : 'slate'} />
        </div>

        {lastRun && (
          <p className="text-xs text-white/50" data-testid="scope-leaks-last-run">
            Last run started {fmt(lastRun.started_at)} · finished {fmt(lastRun.finished_at)}
            {typeof lastRun.alerts_raised === 'number' && lastRun.alerts_raised > 0 && (
              <span className="ml-2 text-rose-300">· raised {lastRun.alerts_raised} broadcast alert</span>
            )}
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-white/40 animate-spin" /></div>
        ) : records.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-8 text-center text-white/50 text-sm" data-testid="scope-leaks-empty">
            No scoped supervisors detected yet — the audit only runs against users with a non-empty <code className="text-teal-300">site_scope</code> or <code className="text-teal-300">assigned_agent_ids</code>.
          </div>
        ) : (
          <div className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/40">
                <tr>
                  <th className="text-left px-4 py-2">Supervisor</th>
                  <th className="text-left px-4 py-2">Expected agents</th>
                  <th className="text-left px-4 py-2">Leaks</th>
                  <th className="text-left px-4 py-2">Audited</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {records.map((r) => (
                  <tr key={r.id || r.user_id} className={r.leak_count > 0 ? 'bg-rose-500/5' : ''} data-testid={`scope-leak-row-${r.user_id}`}>
                    <td className="px-4 py-3 text-white">{r.email || r.user_id}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{(r.expected_agents || []).join(', ') || '—'}</td>
                    <td className="px-4 py-3">
                      {r.leak_count > 0 ? (
                        <div className="space-y-1">
                          {Object.entries(r.leaks || {}).map(([probe, ids]) => (
                            <div key={probe} className="text-rose-300 text-xs" data-testid={`scope-leak-${r.user_id}-${probe}`}>
                              <span className="font-semibold">{probe}</span>: {ids.join(', ')}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-300 text-xs">
                          <ShieldCheck className="w-3 h-3" /> No leaks
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{fmt(r.audited_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const SummaryCard = ({ label, value, tone = 'slate', icon: Icon }) => {
  const tones = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
    rose: 'bg-rose-500/10 border-rose-500/30 text-rose-200',
    slate: 'bg-white/5 border-white/10 text-white/70',
  };
  return (
    <div className={`border rounded-xl px-4 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value ?? '—'}</div>
    </div>
  );
};

export default AdminScopeLeaksPage;
